import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import ModalHeader from 'react-bootstrap/ModalHeader';
import ModalTitle from 'react-bootstrap/ModalTitle';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalFooter from 'react-bootstrap/ModalFooter';
import Nav from 'react-bootstrap/Nav';
import Api, { exceptionNotificationAPI } from '../../services/api';
import Button from '../../components/Button';
import { success_message } from '../../components/Toast';
import { JsonTextarea } from './styles';

const ListTreeImportModal = ({
    show,
    onHide,
    listTreeID,
    exampleJson,
    jsonInput,
    setJsonInput,
    onImportJson,
    importingJson,
    onAfterImportSuccess,
}) => {
    const [tab, setTab] = useState('json');
    const [excelFile, setExcelFile] = useState(null);
    const [importingExcel, setImportingExcel] = useState(false);

    const handleClose = () => {
        setTab('json');
        setExcelFile(null);
        onHide();
    };

    const handleExcelImport = async () => {
        if (!listTreeID) {
            success_message('Save the list first, then import items.');
            return;
        }
        if (!excelFile) {
            exceptionNotificationAPI({ response: { data: { message: 'Select an .xlsx file.' } } });
            return;
        }
        setImportingExcel(true);
        try {
            const fd = new FormData();
            fd.append('listTreeID', String(listTreeID));
            fd.append('file', excelFile);
            await Api.post('ListTree/importItemsExcel', fd);
            success_message('Items imported from Excel successfully');
            setExcelFile(null);
            onAfterImportSuccess();
            handleClose();
        } catch (e) {
            exceptionNotificationAPI(e);
        } finally {
            setImportingExcel(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <ModalHeader closeButton>
                <ModalTitle>Import items (replaces all)</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <p className="text-muted small mb-3">
                    Both options replace every existing item in this list tree. Use the Excel template format: first
                    columns are level indices <strong>0, 1, 2, …</strong>; following columns are attributes (e.g.{' '}
                    <strong>UnitMeasure</strong>). Empty cells repeat the value from the row above in that column.
                </p>
                <Nav variant="tabs" className="mb-3" activeKey={tab} onSelect={k => k && setTab(k)}>
                    <Nav.Item>
                        <Nav.Link eventKey="json">JSON</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="excel">Excel (.xlsx)</Nav.Link>
                    </Nav.Item>
                </Nav>

                {tab === 'json' && (
                    <>
                        <p className="text-muted small mb-2">
                            Each object must have <strong>nodeID</strong> and <strong>value</strong>; optionally{' '}
                            <strong>parentNodeID</strong> or nested <strong>items</strong>. Extra properties become
                            ListTreeItemAttr.
                        </p>
                        <JsonTextarea
                            placeholder={exampleJson}
                            value={jsonInput}
                            onChange={e => setJsonInput(e.target.value)}
                        />
                    </>
                )}

                {tab === 'excel' && (
                    <>
                        <label className="form-label">Excel file</label>
                        <input
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="form-control"
                            onChange={e => setExcelFile(e.target.files?.[0] ?? null)}
                        />
                        {excelFile && (
                            <p className="small text-muted mt-2 mb-0">
                                Selected: <strong>{excelFile.name}</strong>
                            </p>
                        )}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Close
                </button>
                {tab === 'json' && (
                    <Button type="button" onClick={onImportJson} disabled={importingJson}>
                        {importingJson ? 'Importing…' : 'Import from JSON'}
                    </Button>
                )}
                {tab === 'excel' && (
                    <Button type="button" onClick={handleExcelImport} disabled={importingExcel}>
                        {importingExcel ? 'Importing…' : 'Import from Excel'}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default ListTreeImportModal;
